// ON-407: the email-confirmation link carries `redirect_to` (the
// `emailRedirectTo` a web signup passed to supabase.auth.signUp, or SITE_URL
// when none was given). Following it blindly would be an open redirect, so it
// is only honoured when it points back at this site.

const ABSOLUTE_HTTP = /^https?:\/\//i

/**
 * Returns the in-app path (`pathname + search + hash`) to navigate to, or null
 * when `raw` must not be followed. Only an absolute http(s) URL whose origin
 * equals `origin` (scheme, host and port) is accepted. Never throws.
 *
 * Also null for:
 * - the bare site root — app signups send no emailRedirectTo, so the backend
 *   fills in SITE_URL; those must keep landing on /confirm-email (the app
 *   hand-off), not on the web home page;
 * - /auth/* routes, so a link can never loop back into a callback.
 */
export function sameOriginRedirectPath(raw: string | null | undefined, origin: string): string | null {
  if (!raw) return null
  let candidate = raw.trim()
  if (!ABSOLUTE_HTTP.test(candidate)) {
    // Tolerate one extra round of percent-encoding.
    try {
      candidate = decodeURIComponent(candidate).trim()
    } catch {
      return null
    }
    if (!ABSOLUTE_HTTP.test(candidate)) return null
  }

  let url: URL
  let expected: string
  try {
    url = new URL(candidate)
    expected = new URL(origin).origin
  } catch {
    return null
  }
  if (url.origin !== expected) return null
  if (url.pathname === '/' || url.pathname === '') return null
  if (url.pathname === '/auth' || url.pathname.startsWith('/auth/')) return null
  return `${url.pathname}${url.search}${url.hash}`
}
